import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ParkingMall, ParkingSession } from '../models/parking-mall.model';
import { User, Admin, AuthResponse } from '../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private baseUrl = 'https://api.dev.urbix-x.com';

  constructor(private http: HttpClient) {}

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('admin_token');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    });
  }

  // Admin Authentication
  adminLogin(login: string, password: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/admin/auth/login`, { login, password });
  }

  // Users Management
  getUsers(): Observable<User[]> {
    return this.http.get<User[]>(`${this.baseUrl}/users`, { headers: this.getHeaders() });
  }

  getUserById(id: string): Observable<User> {
    return this.http.get<User>(`${this.baseUrl}/users/${id}`, { headers: this.getHeaders() });
  }

  createUser(user: Partial<User>): Observable<User> {
    return this.http.post<User>(`${this.baseUrl}/users`, user, { headers: this.getHeaders() });
  }

  updateUser(id: string, user: Partial<User>): Observable<User> {
    return this.http.patch<User>(`${this.baseUrl}/users/${id}`, user, { headers: this.getHeaders() });
  }

  deleteUser(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/users/${id}`, { headers: this.getHeaders() });
  }

  // Parking Malls Management
  getParkingMalls(): Observable<ParkingMall[]> {
    return this.http.get<ParkingMall[]>(`${this.baseUrl}/parking-mall`);
  }

  getParkingMallById(id: string): Observable<ParkingMall> {
    return this.http.get<ParkingMall>(`${this.baseUrl}/parking-mall/${id}`);
  }

  createParkingMall(mall: Partial<ParkingMall>): Observable<ParkingMall> {
    return this.http.post<ParkingMall>(`${this.baseUrl}/parking-mall`, mall, { headers: this.getHeaders() });
  }

  updateParkingMall(id: string, mall: Partial<ParkingMall>): Observable<ParkingMall> {
    return this.http.patch<ParkingMall>(`${this.baseUrl}/parking-mall/${id}`, mall, { headers: this.getHeaders() });
  }

  deleteParkingMall(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/parking-mall/${id}`, { headers: this.getHeaders() });
  }

  // Parking Sessions
  getActiveSessions(): Observable<ParkingSession[]> {
    return this.http.get<ParkingSession[]>(`${this.baseUrl}/parking-mall/session/active`, { headers: this.getHeaders() });
  }

  getSessionHistory(limit: number = 20): Observable<ParkingSession[]> {
    return this.http.get<ParkingSession[]>(`${this.baseUrl}/parking-mall/session/history?limit=${limit}`, { headers: this.getHeaders() });
  }

  startSession(mallId: string, carId: string): Observable<ParkingSession> {
    return this.http.post<ParkingSession>(`${this.baseUrl}/parking-mall/session/start`, 
      { mallId, carId }, 
      { headers: this.getHeaders() }
    );
  }

  stopSession(sessionId: string): Observable<ParkingSession> {
    return this.http.post<ParkingSession>(`${this.baseUrl}/parking-mall/session/stop/${sessionId}`, 
      {}, 
      { headers: this.getHeaders() }
    );
  }

  // Admin Managers
  getAdmins(): Observable<Admin[]> {
    return this.http.get<Admin[]>(`${this.baseUrl}/admin/managers`, { headers: this.getHeaders() });
  }

  createAdmin(admin: Partial<Admin>): Observable<Admin> {
    return this.http.post<Admin>(`${this.baseUrl}/admin/managers`, admin, { headers: this.getHeaders() });
  }

  updateAdmin(id: string, admin: Partial<Admin>): Observable<Admin> {
    return this.http.put<Admin>(`${this.baseUrl}/admin/managers/${id}`, admin, { headers: this.getHeaders() });
  }

  deleteAdmin(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/admin/managers/${id}`, { headers: this.getHeaders() });
  }
}
